<template>
  <!--  main div  -->
  <div class="d-flex flex-column justify-content-start" style="height: 100%" id="parent">

    <!--  body div  -->
    <v-card style="height: 100%" rounded elevation="2" class="course-registration-card">
      <v-card-title class="text-h5 font-weight-bold primary--text">Course Registration</v-card-title>
      <v-divider></v-divider>
      <div class="flex-grow-1 d-flex flex-column" style="height: 100%">
        <v-tabs v-model="tab" class="d-flex flex flex-column flex-grow-1" style="height: 100%" :show-arrows="isMobile" color="primary" >
          <v-tab key="result" class="no-print text-capitalize">Registration</v-tab>
          <v-tab key="starts" class="no-print text-capitalize">Course Form & Exam Card</v-tab>
          <v-tabs-items v-model="tab" class="d-flex flex flex-column flex-grow-1" style="height: 100%">
            <v-tab-item key="result" style="height: 100%" class="no-print">
              <v-card class="d-flex flex-column flex" style="height: 100%">
                <v-divider class="mb-4"></v-divider>
                <v-row class="px-4">
                  <v-col cols="12" sm="12" md="6" :class="{'h-100': !isMobile}">
                    <v-sheet width="100%" height="100%" elevation="0" class="course-section">
                      <div class="section-header mb-4">
                        <h3 class="text-h6 font-weight-bold">Available Courses</h3>
                        <v-chip small color="primary" text-color="white">{{ courses.length }} courses</v-chip>
                      </div>
                      <v-card-text>
                        <div class="d-flex flex-row flex-wrap flex-md-nowrap mb-4 justify-space-between gap-2">
                          <x-select
                            outlined
                            class="flex-grow-1 mr-md-2"
                            :items="sessions"
                            v-model="selectedSession"
                            dense
                            item-text="name"
                            return-object
                            placeholder="Select Session"
                            prepend-inner-icon="mdi-calendar"
                          ></x-select>
                          <x-select
                            outlined
                            class="flex-grow-1"
                            :items="semesters"
                            v-model="selectedSemester"
                            dense
                            item-text="title"
                            return-object
                            placeholder="Select Semester"
                            prepend-inner-icon="mdi-book-open-variant"
                          ></x-select>
                        </div>
                        <x-text-field
                          outlined
                          dense
                          v-model="search"
                          placeholder="Search Course..."
                          prepend-inner-icon="mdi-magnify"
                          clearable
                          class="mb-4"
                        ></x-text-field>
                        <div class="courses-list">
                          <v-card
                            v-for="(item, index) in courses"
                            :key="index"
                            outlined
                            class="mb-3 course-card"
                            :class="{'disabled-course': !selectedSemester.open || loading || creditUnitAvailable < item.creditUnit}"
                            hover
                          >
                            <v-card-text class="pa-3">
                              <div class="d-flex align-center">
                                <v-checkbox
                                  @click="registerCourse(item)"
                                  v-model="chbTrackers[item.courseCode]"
                                  true-value="1"
                                  false-value="0"
                                  :disabled="!selectedSemester.open || loading || creditUnitAvailable < item.creditUnit"
                                  class="mt-0 pt-0"
                                  hide-details
                                ></v-checkbox>
                                <div class="flex-grow-1 ml-2">
                                  <div class="d-flex flex-row justify-space-between align-center">
                                    <span class="font-weight-bold text-body-1">{{item.courseCode}}<span v-if="item.alias != null" class="text-caption ml-1">(Formerly {{item.alias.courseCode}})</span></span>
                                    <v-chip small color="primary" outlined>{{item.creditUnit}} Credit{{item.creditUnit > 1 ? 's' : ''}}</v-chip>
                                  </div>
                                  <div class="text-body-2 text--secondary mt-1">{{item.title}}</div>
                                </div>
                              </div>
                            </v-card-text>
                          </v-card>
                        </div>
                      </v-card-text>
                    </v-sheet>
                  </v-col>
                  <v-col cols="12" sm="12" md="6" :class="{'h-100': !isMobile}">
                    <v-sheet width="100%" height="100%" elevation="0" class="registered-section">
                      <div class="section-header mb-4">
                        <h3 class="text-h6 font-weight-bold">Registered Courses</h3>
                        <v-chip small color="success" dark class="font-weight-medium">
                          <v-icon left x-small>mdi-check-circle</v-icon>
                          {{ registeredCourses.length }} registered
                        </v-chip>
                      </div>
                      <v-card-text>
                        <div v-if="registeredCourses.length < 1" class="text-center py-8">
                          <v-icon size="64" color="grey lighten-2">mdi-school-outline</v-icon>
                          <p class="text-h6 grey--text text--lighten-1 mt-4">No courses registered yet</p>
                          <p class="text-body-2 grey--text">Select courses from the left to register</p>
                        </div>
                        <v-alert
                          v-if="registeredCourses.some(c => c.status === 'REJECTED')"
                          color="orange lighten-5"
                          border="left"
                          colored-border
                          elevation="2"
                          icon="mdi-alert-circle"
                          class="mb-4 rejection-alert"
                        >
                          <div class="d-flex align-center justify-space-between">
                            <span class="font-weight-medium">Remove rejected registrations</span>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  color="orange darken-2"
                                  v-bind="attrs"
                                  v-on="on"
                                  small
                                >
                                  mdi-information
                                </v-icon>
                              </template>
                              <span>Rejected courses need to be removed before new registration</span>
                            </v-tooltip>
                          </div>
                        </v-alert>
                        <v-card v-if="registeredCourses.length > 0" class="mb-4 stats-card gradient-card" elevation="4">
                          <v-card-text>
                            <v-row no-gutters>
                              <v-col cols="12" sm="4" class="text-center">
                                <div class="stat-item">
                                  <div class="stat-icon-wrapper primary">
                                    <v-icon color="white" size="24">mdi-book-education</v-icon>
                                  </div>
                                  <div class="text-h4 font-weight-bold mt-2">{{coursesCount}}</div>
                                  <div class="text-body-2 text--secondary">Total Courses</div>
                                </div>
                              </v-col>
                              <v-col cols="12" sm="4" class="text-center">
                                <div class="stat-item">
                                  <div class="stat-icon-wrapper orange">
                                    <v-icon color="white" size="24">mdi-sigma</v-icon>
                                  </div>
                                  <div class="text-h4 font-weight-bold mt-2">{{creditLoadCount}}</div>
                                  <div class="text-body-2 text--secondary">Credit Load</div>
                                </div>
                              </v-col>
                              <v-col cols="12" sm="4" class="text-center" v-if="semesterSetting != null">
                                <div class="stat-item">
                                  <div class="stat-icon-wrapper green">
                                    <v-icon color="white" size="24">mdi-check-circle</v-icon>
                                  </div>
                                  <div class="text-h4 font-weight-bold mt-2">{{creditUnitAvailable}}</div>
                                  <div class="text-body-2 text--secondary">Units Available</div>
                                </div>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                        <div class="registered-courses-list">
                          <transition-group name="list-fade" tag="div">
                            <v-card
                              v-for="(item, index) in registeredCourses"
                              :key="item.courseCode"
                              class="mb-3 registered-course-card"
                              :class="{
                                'rejected-course': item.status === 'REJECTED',
                                'approved-course': item.status === 'APPROVED',
                                'pending-course': item.status === 'PENDING'
                              }"
                              elevation="2"
                            >
                              <v-card-text class="pa-4">
                                <div class="d-flex align-start">
                                  <v-btn
                                    icon
                                    :color="item.status === 'REJECTED' ? 'error' : 'grey'"
                                    @click="unRegisterCourse(item)"
                                    :disabled="loading || (item.status === 'APPROVED' && semesterSetting?.approvalEnabled)"
                                    class="mr-3 remove-btn"
                                    :class="{'pulse-animation': item.status === 'REJECTED'}"
                                  >
                                    <v-icon>mdi-minus-circle</v-icon>
                                  </v-btn>
                                  <div class="flex-grow-1">
                                    <div class="d-flex flex-column flex-sm-row justify-space-between mb-2">
                                      <div class="d-flex align-center mb-2 mb-sm-0">
                                        <h4 class="text-h6 font-weight-bold mr-2">{{item.courseCode}}</h4>
                                        <v-chip
                                          small
                                          :color="item.status === 'APPROVED' ? 'success' : item.status === 'REJECTED' ? 'error' : item.status === 'PENDING' ? 'warning' : 'grey'"
                                          :text-color="item.status === 'PENDING' ? 'black' : 'white'"
                                          class="status-chip"
                                        >
                                          <v-icon left x-small>
                                            {{ item.status === 'APPROVED' ? 'mdi-check' : item.status === 'REJECTED' ? 'mdi-close' : 'mdi-clock-outline' }}
                                          </v-icon>
                                          {{item.status}}
                                        </v-chip>
                                      </div>
                                      <div class="d-flex align-center">
                                        <div v-if="item.status === 'REJECTED'" class="error--text font-weight-medium d-flex align-center">
                                          <v-icon small color="error" class="mr-1">mdi-alert-triangle</v-icon>
                                          Remove this course
                                        </div>
                                        <div v-else class="credit-badge">
                                          <span class="font-weight-bold">{{item.creditUnit}}</span>
                                          <span class="text-caption ml-1">Credit{{item.creditUnit > 1 ? 's' : ''}}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <p class="text-body-2 text--secondary mb-0">{{item.title}}</p>
                                  </div>
                                </div>
                              </v-card-text>
                            </v-card>
                          </transition-group>
                        </div>
                      </v-card-text>
                    </v-sheet>
                  </v-col>
                </v-row>

              </v-card>
            </v-tab-item>

            <v-tab-item key="stats">
              <v-divider class="mb-4"></v-divider>
              <course-form-and-exam-card :courses="registeredCourses" :user="getUser" :sessions="sessions"></course-form-and-exam-card>
            </v-tab-item>
          </v-tabs-items>
        </v-tabs>
      </div>
    </v-card>
    <loading-dialog :show="loading"></loading-dialog>
    <message-box :show="messageShow" :message="message" :type="messageType" v-on:close-message="messageShow = !messageShow"></message-box>
  </div>
</template>

<script>

import Api from "@/main/api";
import ErrorReslover from "@/shared/ErrorResolver";
import LoadingDialog from "@ui/components/LoadingDialog";
import MessageBox from "@ui/components/MessageBox";
import {mapGetters} from "vuex";
import CourseFormAndExamCard from "../components/CourseFormAndExamCard";
import XSelect from '@ui/widgets/XSelect.vue';
import XTextField from "@ui/widgets/XTextField.vue";

export default {
  components: {CourseFormAndExamCard, MessageBox, LoadingDialog, XSelect, XTextField},
  name: "CourseRegistration",
  data: () => ({
    singleSelect: false,
    search: '',
    tab: null,
    itemsPerPage: 3,
    page: 1,
    pageCount: 4,
    showAddResult: false,
    showModerateScore: false,
    messageType: '',
    message: '',
    messageShow: false,
    loading: false,
    coursesCount: 0,
    creditLoadCount: 0,
    semesterSetting: null,
    headers: [
      {
        text: 'Matriculation Number',
        align: 'start',
        value: 'user.matric',
      },
      { text: 'CA', value: 'ca', sortable: true  },
      { text: 'Exam', value: 'exam', sortable: true  },
      { text: 'Moderated', value: 'moderated', sortable: false },
      { text: 'Total', value: 'total', sortable: true },
      { text: 'Grade', value: 'grade', sortable: true },
    ],

    registeredCourses: [],
    tmpRegisteredCourses: [],
    chbTrackers: [],
    chbTrackersU: [],
    courses: [],
    mainCourses: [],
    tmpCourses: [],
    sessions: [],
    selectedSession: null,
    semesters: [],
    selectedSemester: null
  }),

  methods: {
    fetchCourses(){
      if(this.getUser != null && this.selectedSemester != null && this.selectedSession != null) {
        this.loading = true;
        this.courses = []
        this.tmpCourses = []
        this.semesterSetting = null
        this.registeredCourses = []
        Api.course.allForStudent(this.getUser.userId, this.selectedSession.id, this.selectedSemester.id).then(res => {
          this.courses = res.data.courseList
          this.tmpCourses = res.data.courseList
          this.semesterSetting = res.data.semesterSetting
          this.registeredCourses = res.data.registeredCourses
        })
            .catch(err => ErrorReslover.checkErrors(this, err))
            .finally(() => this.loading = false);
      }
    },

    registerCourse(course){
      let uId = this.getUser.userId
      if(uId && this.selectedSession != null) {
        this.loading = true;
        Api.students.registerCourse({
          'studentMatric': uId,
          'course': course.id,
          'sessionId': this.selectedSession.id,
          'semesterId': this.selectedSemester.id
        })
            .then(res => {
              this.messageType = 'success'
              this.message = "Course registered successfully"
              this.messageShow = true
              // add to list of registered courses
              this.registeredCourses.push(course)
              this.courses = this.courses.filter(e => e.id !== course.id)
              res.data
            }).catch(err => {
          ErrorReslover.checkErrors(this, err)
        })
            .finally(() => {
              this.loading = false;
              this.chbTrackers[course.courseCode] = 0
            })
      }
    },

    unRegisterCourse(course){
      let uId = this.getUser.userId
      if(uId && this.selectedSemester != null) {
        this.loading = true;
        Api.students.unRegisterCourse({
          'studentMatric': uId,
          'course': course.id,
          'semesterId': this.selectedSemester.id
        })
            .then(res => {
              this.messageType = 'success'
                this.message = 'Registration removed'
              this.messageShow = true
              // add to list of registered courses
              this.courses.push(course)
              this.registeredCourses = this.registeredCourses.filter(e => e.courseCode !== course.courseCode)
              res.data
            }).catch(err => ErrorReslover.checkErrors(this, err))
            .finally(() => {
              this.loading = false;
            })
      }
    },

    loadSemesters(){
      this.semesters = []
      if(this.selectedSession){
        this.loading = true;
        Api.semester.getPaidSemesters(this.getUser.userId, this.selectedSession.id)
          .then(res => this.semesters = res.data.filter(e => e.open))
          .catch(err => ErrorReslover.checkErrors(this, err))
          .finally(() => this.loading = false);

      }
    },

    hasCourse(arr, el){
      for (let i = 0; i < arr.length; i++) {
        if(arr[i].courseCode === el.courseCode){
          return true;
        }
      }

      return false;
    },

    getSessions(){
      if(this.getUser != null){
        Api.session.getRegisteredSessionsForStudent(this.getUser.userId)
        .then(res => {
          this.sessions = res.data
          // console.log(res.data)
        })
        .catch(err => ErrorReslover.checkErrors(this, err))
      }
    }
  },

  computed:{
    ...mapGetters(['getUser', 'getCurrentSession', 'getCurrentSession']),

    creditUnitAvailable: function(){
      if(this.semesterSetting == null)
        return 0;

      return this.semesterSetting.totalCreditUnit - this.creditLoadCount
    },

    isMobile: function(){
      return this.$vuetify.breakpoint.smAndDown
    },

    isMedium: function(){
      return this.$vuetify.breakpoint.mdOnly
    },

    isMediumAndDown: function () {
      return this.$vuetify.breakpoint.sm
    },


    isMediumAndUp: function () {
      return this.$vuetify.breakpoint.mdAndUp
    },

  },

  watch:{
    selectedSession: function(newVal){
      this.selectedSemester = null
      this.semesters = []
      if(newVal != null){
        this.loadSemesters()
        // this.fetchCourses()
      }
    },

    registeredCourses: function(newVal){
      if(newVal != null){
        this.coursesCount = this.registeredCourses.length
        if(newVal.length > 0){
          this.creditLoadCount = this.registeredCourses.map(e => e.creditUnit).reduce((p, c) => p + c, 0)
        }
      }
    },

    selectedSemester: function(newVal){
      this.courses = []
      this.registeredCourses = []
      if(newVal != null){
        this.fetchCourses()
      }
    },

    search: function(newVal){
      if(newVal != null){
        this.courses = this.tmpCourses.filter(e => !this.hasCourse(this.registeredCourses, e) &&
            (e.courseCode.toLowerCase().includes(newVal.toLowerCase()) || e.title.toLowerCase().includes(newVal.toLowerCase()))
        );
      }
    }
  },

  created() {
    this.getSessions()
  }
}
</script>

<style scoped>
.v-btn{
  text-transform: none;
}

@media print {
  .no-print{
    display: none;
  }
}

.h-100{
  height: 100%;
}

.course-registration-card {
  background-color: #fafafa;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;
}

.course-card {
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
}

.course-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.disabled-course {
  opacity: 0.6;
  pointer-events: none;
}

.registered-course-card {
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
}

.rejected-course {
  background: linear-gradient(to right, #fff5f5, #ffe0e0);
  border-left: 4px solid #ef5350;
}

.approved-course {
  background: linear-gradient(to right, #f1f8f4, #e8f5e9);
  border-left: 4px solid #66bb6a;
}

.pending-course {
  background: linear-gradient(to right, #fffbf0, #fff8e1);
  border-left: 4px solid #ffa726;
}

.rejection-alert {
  border-radius: 8px;
}

.status-chip {
  font-size: 11px;
  height: 22px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.credit-badge {
  background-color: #e3f2fd;
  color: #1976d2;
  padding: 4px 12px;
  border-radius: 16px;
  display: inline-flex;
  align-items: baseline;
}

.remove-btn {
  transition: all 0.3s ease;
}

.remove-btn:hover:not(:disabled) {
  transform: scale(1.1);
}

.pulse-animation {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.list-fade-enter-active, .list-fade-leave-active {
  transition: all 0.5s ease;
}

.list-fade-enter-from, .list-fade-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.list-fade-move {
  transition: transform 0.5s;
}

.stats-card {
  border-radius: 16px;
  overflow: hidden;
}

.gradient-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.gradient-card .text--secondary {
  color: rgba(255, 255, 255, 0.8) !important;
}

.stat-item {
  padding: 16px;
}

.stat-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon-wrapper.primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.stat-icon-wrapper.orange {
  background: linear-gradient(135deg, #f093fb, #f5576c);
}

.stat-icon-wrapper.green {
  background: linear-gradient(135deg, #4facfe, #00f2fe);
}

.courses-list {
  max-height: 600px;
  overflow-y: auto;
  padding-right: 4px;
}

.registered-courses-list {
  max-height: 500px;
  overflow-y: auto;
  padding-right: 8px;
  padding-left: 4px;
}

.courses-list::-webkit-scrollbar,
.registered-courses-list::-webkit-scrollbar {
  width: 6px;
}

.courses-list::-webkit-scrollbar-track,
.registered-courses-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.courses-list::-webkit-scrollbar-thumb,
.registered-courses-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.courses-list::-webkit-scrollbar-thumb:hover,
.registered-courses-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.gap-2 {
  gap: 8px;
}

</style>
